import { Location } from '@angular/common';
import { HttpBackend, HttpClient, HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, firstValueFrom, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { loginRedirectExtras } from '../auth/return-url.util';
import { apiErrorFallback, extractApiErrorMessage } from '../http/extract-api-error-message';
import { Translation } from '../i18n/translation.service';
import { normalizeStoredLanguage, readStoredLanguage } from '../i18n/language-storage';
import type { RefreshTokenRequestDto } from '../models/auth/refresh-token-request.dto';
import { NotificationHubService } from '../realtime/notification-hub.service';
import { TokenStorageService } from '../storage/token-storage.service';

const REFRESH_PATH = '/api/Accounts/RefreshToken';
const AUTH_RETRY_HEADER = 'X-Auth-Retry';

/** Paths that must not trigger refresh (public or auth bootstrap). */
const NO_REFRESH_URL_PARTS: readonly string[] = [
  '/api/Accounts/Login',
  '/api/Accounts/LoginByTenantId',
  '/api/Accounts/Logout',
  '/api/Accounts/ForgetPassword',
  '/api/Accounts/ForgetPasswordByTenant',
  '/api/Accounts/SendOTP',
  '/api/Accounts/ValidateOtp',
  '/api/Accounts/ResetPassword',
  '/api/Accounts/ValidateChangePassword',
  '/api/Accounts/SetFirstPassword',
  '/api/Accounts/RequestActivation',
  '/api/Accounts/AcceptInvitation',
  '/api/Accounts/RefreshToken',
  '/api/Tenants/RegisterTenant',
];

let refreshInFlight: Promise<{ accessToken: string; refreshToken: string }> | null = null;

function apiBase(): string {
  return environment.apiBaseUrl.replace(/\/+$/, '');
}

function extractTokenPair(body: unknown): { accessToken: string; refreshToken: string } | null {
  if (!body || typeof body !== 'object') {
    return null;
  }
  const root = body as { data?: unknown; accessToken?: string; refreshToken?: string };
  const inner = root.data !== undefined ? root.data : body;
  if (!inner || typeof inner !== 'object') {
    return null;
  }
  const access = (inner as { accessToken?: string }).accessToken;
  const refresh = (inner as { refreshToken?: string }).refreshToken;
  if (typeof access !== 'string' || typeof refresh !== 'string' || !access || !refresh) {
    return null;
  }
  return { accessToken: access, refreshToken: refresh };
}

function isUnauthorized(err: HttpErrorResponse): boolean {
  if (err.status === 401) {
    return true;
  }
  const e = err.error;
  if (e && typeof e === 'object' && 'statusCode' in e) {
    const code = (e as { statusCode?: number }).statusCode;
    return code === 401;
  }
  return false;
}

function isNetworkFailure(err: unknown): boolean {
  return err instanceof HttpErrorResponse && err.status === 0;
}

function shouldSkipRefresh(req: HttpRequest<unknown>): boolean {
  if (req.headers.has(AUTH_RETRY_HEADER)) {
    return true;
  }
  const url = req.url;
  return NO_REFRESH_URL_PARTS.some((part) => url.includes(part));
}

/** Prefer `Location.path()` so `returnUrl` matches the real URL (same idea as `authGuard` using `state.url`). */
function redirectToLogin(router: Router, location: Location): void {
  const url = location.path() || '/';
  void router.navigate(['/login'], loginRedirectExtras(url));
}

function refreshSession(
  plainHttp: HttpClient,
  tokens: TokenStorageService,
  acceptLanguage: string,
  hub: NotificationHubService,
): Promise<{ accessToken: string; refreshToken: string }> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const refresh = tokens.getRefreshToken();
      const user = tokens.readUser();
      if (!refresh || !user?.id) {
        void hub.disconnect();
        tokens.clearTokens();
        throw new Error('Missing refresh context');
      }
      const body: RefreshTokenRequestDto = { userId: user.id, refreshToken: refresh };
      const raw = await firstValueFrom(
        plainHttp.post<unknown>(`${apiBase()}${REFRESH_PATH}`, body, {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'Accept-Language': acceptLanguage,
          },
        }),
      );
      const pair = extractTokenPair(raw);
      if (!pair) {
        void hub.disconnect();
        tokens.clearTokens();
        throw new Error('Invalid refresh response');
      }
      tokens.saveTokens(pair.accessToken, pair.refreshToken);
      hub.reconnectAfterTokenRefresh();
      return pair;
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

const AUTH_LAST_ERROR_KEY = 'worknest.auth.lastError';

export const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const tokens = inject(TokenStorageService);
  const backend = inject(HttpBackend);
  const translate = inject(TranslateService);
  const translation = inject(Translation);
  const router = inject(Router);
  const location = inject(Location);
  const notificationHub = inject(NotificationHubService);
  const plainHttp = new HttpClient(backend);

  const stored = readStoredLanguage();
  const fromTranslate = translate.getCurrentLang() || translate.getFallbackLang() || 'en-US';
  const acceptLanguage = normalizeStoredLanguage(stored ?? fromTranslate);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse)) {
        return throwError(() => err);
      }
      if (shouldSkipRefresh(req)) {
        return throwError(() => err);
      }
      if (!isUnauthorized(err)) {
        return throwError(() => err);
      }
      return from(refreshSession(plainHttp, tokens, acceptLanguage, notificationHub)).pipe(
        switchMap(() => {
          const access = tokens.getAccessToken();
          if (!access) {
            void notificationHub.disconnect();
            tokens.clearTokens();
            redirectToLogin(router, location);
            return throwError(() => err);
          }
          const retry = req.clone({
            setHeaders: {
              Authorization: `Bearer ${access}`,
              [AUTH_RETRY_HEADER]: '1',
            },
          });
          return next(retry);
        }),
        catchError((refreshErr: unknown) => {
          if (isNetworkFailure(refreshErr)) {
            return throwError(() => refreshErr);
          }

          try {
            const msg = extractApiErrorMessage(err, apiErrorFallback(translation));
            if (msg.trim().length > 0) {
              sessionStorage.setItem(AUTH_LAST_ERROR_KEY, msg);
            }
          } catch {
            // ignore storage / parse errors
          }
          void notificationHub.disconnect();
          tokens.clearTokens();
          redirectToLogin(router, location);
          return throwError(() => err);
        }),
      );
    }),
  );
};
